import { useEffect, useRef } from 'react';
import { Button } from '@practium/ui';
import styles from './ConfirmModal.module.css';

interface ConfirmModalProps {
	isOpen: boolean;
	title: string;
	message: string;
	confirmText?: string;
	cancelText?: string;
	onConfirm: () => void;
	onCancel: () => void;
	variant?: 'danger' | 'warning' | 'info';
}

export const ConfirmModal = ({
	isOpen,
	title,
	message,
	confirmText = 'Confirmar',
	cancelText = 'Cancelar',
	onConfirm,
	onCancel,
	variant = 'danger',
}: ConfirmModalProps) => {
	const cancelButtonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (isOpen) {
			cancelButtonRef.current?.focus();
		}
	}, [isOpen]);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isOpen) {
				onCancel();
			}
		};

		document.addEventListener('keydown', handleEscape);
		return () => document.removeEventListener('keydown', handleEscape);
	}, [isOpen, onCancel]);

	if (!isOpen) return null;

	return (
		<div className={styles.overlay} onClick={onCancel}>
			<div className={styles.modal} onClick={(e) => e.stopPropagation()}>
				<div className={styles.header}>
					<h2 className={styles.title}>{title}</h2>
				</div>

				<div className={styles.content}>
					<p className={styles.message}>{message}</p>
				</div>

				<div className={styles.footer}>
					<Button
						variant="cancel"
						ref={cancelButtonRef}
						className={styles.cancelButton}
						onClick={onCancel}
						type="button"
					>
						{cancelText}
					</Button>
					<Button
						variant={
							variant === 'danger' ? 'dangerSolid' : variant === 'warning' ? 'warning' : 'info'
						}
						className={styles.confirmButton}
						onClick={onConfirm}
						type="button"
					>
						{confirmText}
					</Button>
				</div>
			</div>
		</div>
	);
};
